import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Text as Typography } from './Text';
import {
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAppNavigation } from '../../hooks/useAppNavigation';
import { useUIStateStore } from '../../stores/useUIStateStore';
import { useThemeStore } from '../../stores/useThemeStore';
import { useSystemConfigStore } from '../../stores/useSystemConfigStore';
import { SidebarLink } from '../../types';
import { useNotificationStore } from '../../stores/useNotificationStore';
import { APP_PATHS } from '../../config/appRoutes';
import LazyImage from '../common/LazyImage';
import { useLabels } from '../../hooks/useLabels';
import { ROLE_KEYS } from '../../constants/roleKeys';
import { CONFIG_DEFAULTS } from '../../constants';
import { createPrefetchHandlers, resolvePrefetchFactory } from '../../utils/routePrefetcher';

// ============================================================================
// DESIGN CONSTANTS - Professional Grade Transitions (Reactive to Elite Aesthetics)
// ============================================================================
const GLOBAL_EASING = 'ease-[cubic-bezier(0.4,0,0.2,1)]';
const LAYOUT_TRANSITION = `transition-all duration-[var(--ui-animation-speed)] ${GLOBAL_EASING}`;
const HOVER_TRANSITION = `transition-all duration-[var(--ui-animation-speed)] ${GLOBAL_EASING}`;

const SIDEBAR_BG = 'bg-theme-bg-secondary';
const CURSOR_INTERACTIVE = 'cursor-pointer';

/**
 * Active state — smooth background blend with accent border
 * Uses transition-compatible properties for fluid state changes
 */
const ACTIVE_STATE_CLASSES = `
  bg-theme-primary
  text-theme-sidebar-text
  border-l-4 border-theme-accent
  shadow-dynamic
`;

/**
 * Inactive state — subtle hover feedback with transparent border
 * The transparent border ensures no layout shift when transitioning to active
 */
const INACTIVE_STATE_CLASSES = `
  text-theme-sidebar-text text-opacity-80
  hover:bg-theme-primary hover:bg-opacity-10
  hover:text-theme-sidebar-text
  border-l-4 border-transparent
`;

const getSubItemIconWrapper = (icon: React.ReactNode): React.ReactNode => {
  const iconClass = `w-3.5 h-3.5 flex-shrink-0 text-theme-sidebar-icon text-opacity-70 group-hover:text-theme-sidebar-icon group-hover:text-opacity-100 ${HOVER_TRANSITION} group-hover:scale-[var(--interactive-scale)]`;
  if (!React.isValidElement(icon)) return icon;
  return React.cloneElement(icon, {
    className: iconClass,
    strokeWidth: 2.5
  } as React.HTMLAttributes<SVGElement>);
};

// ============================================================================
// NAVIGATION ITEM COMPONENT
// ============================================================================
interface NavItemProps {
  link: SidebarLink;
  isSidebarExpanded: boolean;
  isParentOpen: boolean;
  toggleParent: (name: string) => void;
  currentPath: string;
  onNavigate: (path: string) => void;
}

const NavItem = memo(({
  link,
  isSidebarExpanded,
  isParentOpen,
  toggleParent,
  currentPath,
  onNavigate,
}: NavItemProps) => {
  const { getLabel } = useLabels();

  // PREDICTIVE PRE-FETCH: Resolve the chunk factory for this link's route path.
  // Returns debounced onMouseEnter/onMouseLeave handlers for hover-triggered pre-loading.
  const prefetchHandlers = useMemo(() => {
    if (!link.path) return undefined;
    const factory = resolvePrefetchFactory(link.path);
    if (!factory) return undefined;
    return createPrefetchHandlers(link.path, factory);
  }, [link.path]);

  const isDirectlyActive = link.path
    ? currentPath === link.path || currentPath.startsWith(link.path + '/')
    : false;

  const hasSubLinks = link.subLinks && link.subLinks.length > 0;
  const hasActiveSubLink = hasSubLinks && link.subLinks?.some(
    (sub) => sub.path && (currentPath === sub.path || currentPath.startsWith(sub.path + '/'))
  );

  const isActive = isDirectlyActive || hasActiveSubLink;
  const isExpanded = isParentOpen;

  const getItemClasses = (): string => {
    const isInteractive = !!link.path || hasSubLinks;
    /**
     * Professional centering & rounding logic:
     * - Expanded: left-aligned with right-only rounding (sidebar edge aesthetic)
     * - Collapsed: centered icon rail with full rounding for neat icon buttons
     */
    const alignment = isSidebarExpanded
      ? 'px-3 justify-start rounded-r-lg'
      : 'px-0 justify-center rounded-lg mx-1';
    const baseLayout = `w-full py-2.5 flex items-center gap-3 group relative active:scale-[0.98] origin-left ${alignment} ${isInteractive ? CURSOR_INTERACTIVE : ''}`;

    if (isActive) {
      return `${baseLayout} ${HOVER_TRANSITION} ${ACTIVE_STATE_CLASSES}`;
    }

    return `${baseLayout} ${HOVER_TRANSITION} ${INACTIVE_STATE_CLASSES}`;
  };

  const handleClick = (e: React.MouseEvent) => {
    if (hasSubLinks) {
      e.preventDefault();
      toggleParent(link.name);
    } else if (link.path) {
      onNavigate(link.path);
    }
  };

  return (
    <div className="mb-1">
      <Link
        to={hasSubLinks ? '#' : link.path || '#'}
        onClick={handleClick}
        onMouseEnter={prefetchHandlers?.onMouseEnter}
        onMouseLeave={prefetchHandlers?.onMouseLeave}
        className="block"
        style={{ textDecoration: 'none' }}
      >
        {/* Tooltip on collapsed: native title provides accessible label for icon-only rail */}
        <button
          type="button"
          className={getItemClasses()}
          title={!isSidebarExpanded ? getLabel(link.name) : undefined}
        >
          {/* Icon — color adapts to active state for clear visual hierarchy */}
          {link.icon && (
            <span className={`flex-shrink-0 w-5 h-5 ${isActive ? 'text-white' : 'text-theme-sidebar-icon'} ${HOVER_TRANSITION} group-hover:scale-[var(--interactive-scale)]`}>
              {React.cloneElement(link.icon as React.ReactElement, {
                className: 'w-5 h-5',
                strokeWidth: 2
              })}
            </span>
          )}

          {/* Text label — opacity fade, clipped by parent overflow-hidden */}
          <div className={`flex-1 flex items-center justify-between min-w-0 overflow-hidden ${LAYOUT_TRANSITION} ${isSidebarExpanded ? 'opacity-100' : 'opacity-0 w-0'}`}>
            <Typography
              className="truncate"
              variant="body-sm"
              weight="medium"
              align="left"
              truncate
            >
              {getLabel(link.name)}
            </Typography>
            {link.badgeCount ? (
              <span className="bg-theme-error-accent text-white px-2 py-0.5 rounded-full text-xs font-bold animate-in fade-in zoom-in duration-300 ml-2">
                {link.badgeCount > 99 ? '99+' : link.badgeCount}
              </span>
            ) : null}
          </div>

          {/* Chevron — always in DOM for smooth rotation, hidden when collapsed */}
          {hasSubLinks && (
            <span
              className={`flex-shrink-0 text-theme-sidebar-icon ${HOVER_TRANSITION} ${isExpanded ? 'rotate-180' : 'rotate-0'} ${isSidebarExpanded ? 'opacity-100' : 'opacity-0 w-0'
                }`}
            >
              <ChevronDownIcon className="w-4 h-4" strokeWidth={4.5} />
            </span>
          )}
        </button>
      </Link>

      {/* Sub-link accordion — uses grid-rows for pixel-perfect height animation */}
      {hasSubLinks && isSidebarExpanded && (
        <div
          className={`grid transition-[grid-template-rows,opacity] duration-[var(--ui-animation-speed)] ${GLOBAL_EASING} ${isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
            }`}
        >
          <div className="overflow-hidden">
            <div className="mt-1 ml-4 pl-3 space-y-0.5 border-l border-theme-divider/20">
              {link.subLinks?.map((subLink, index) => {
                const isSubActive = subLink.path &&
                  (currentPath === subLink.path || currentPath.startsWith(subLink.path + '/'));

                // Resolve prefetch handlers for each subLink
                const subPrefetch = subLink.path
                  ? (() => {
                      const factory = resolvePrefetchFactory(subLink.path!);
                      return factory ? createPrefetchHandlers(subLink.path!, factory) : undefined;
                    })()
                  : undefined;

                return (
                  <Link
                    key={subLink.path || subLink.name}
                    to={subLink.path || '#'}
                    onClick={() => subLink.path && onNavigate(subLink.path)}
                    onMouseEnter={subPrefetch?.onMouseEnter}
                    onMouseLeave={subPrefetch?.onMouseLeave}
                    style={{ textDecoration: 'none' }}
                    className="block"
                  >
                    <button
                      type="button"
                      style={{
                        transitionDelay: isExpanded ? `${index * 40}ms` : '0ms'
                      }}
                      className={`w-full px-3 py-2 rounded-lg flex items-center gap-2.5 group active:scale-[0.98] cursor-pointer
                        transition-all duration-200 ${GLOBAL_EASING}
                        ${isSubActive
                          ? 'text-theme-sidebar-text font-semibold bg-theme-primary/80 shadow-sm'
                          : 'text-theme-sidebar-text/70 hover:text-theme-sidebar-text hover:bg-theme-primary/10'
                        }`}
                    >
                      {/* Active indicator dot */}
                      {isSubActive ? (
                        <span className="w-1.5 h-1.5 rounded-full bg-theme-accent flex-shrink-0 animate-in fade-in duration-300" />
                      ) : (
                        <span className={`w-1.5 h-1.5 rounded-full bg-theme-sidebar-icon/30 flex-shrink-0 ${HOVER_TRANSITION} group-hover:bg-theme-sidebar-icon/60`} />
                      )}
                      {subLink.icon ? getSubItemIconWrapper(subLink.icon) : null}
                      <div className="flex-1 flex items-center justify-between min-w-0">
                        <Typography className="truncate" variant="body-sm" align="left">{getLabel(subLink.name)}</Typography>
                        {subLink.badgeCount ? (
                          <span className="bg-theme-error-accent text-white px-1.5 py-0.5 rounded-full text-[10px] font-bold animate-in fade-in zoom-in duration-300 ml-2">
                            {subLink.badgeCount > 99 ? '99+' : subLink.badgeCount}
                          </span>
                        ) : null}
                      </div>
                    </button>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

// ============================================================================
// MAIN SIDEBAR COMPONENT
// ============================================================================
const Sidebar: React.FC = () => {
  const uiState = useUIStateStore();
  const {
    isDesktopSidebarCollapsed,
    isSidebarHovered,
    isMobileSidebarOpen,
    toggleDesktopSidebar,
    sidebarOpenSections,
    toggleSidebarSection,
    setSidebarSectionOpen,
  } = uiState;

  const { user, logout } = useAuth();
  const { links: navigationLinks, isLoading } = useAppNavigation();
  // FIXED: Destructure theme from specialized useTheme hook
  const { general } = useSystemConfigStore();
  const { theme } = useTheme();
  const { resolvedTheme } = useThemeStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { getLabel } = useLabels();
  const { notifications } = useNotificationStore();

  // ----------------------------------------------------------------------------
  // BADGE COMPUTATION ENGINE (GOLDEN STANDARD OBSERVER PATTERN)
  // ----------------------------------------------------------------------------
  const badgeMap = useMemo(() => {
    const map: Record<string, number> = {};
    const unread = notifications.filter(n => n.status === 'UNREAD');
    
    // Total Unread Count
    const totalUnread = unread.length;
    // Domain-Specific Counts
    const feedbackCount = unread.filter(n => n.type === 'FEEDBACK_UPDATE').length;
    
    if (totalUnread > 0) {
      map[APP_PATHS.STUDENT.NOTIFICATIONS] = totalUnread;
      map[APP_PATHS.STAFF.NOTIFICATIONS] = totalUnread;
      map[APP_PATHS.ADMIN.NOTIFICATIONS] = totalUnread;
    }
    
    if (feedbackCount > 0) {
      map[APP_PATHS.STAFF.FEEDBACK.ROOT] = feedbackCount;
      map[APP_PATHS.ADMIN.FEEDBACK_MANAGEMENT] = feedbackCount;
      map[APP_PATHS.STUDENT.SUBMIT_FEEDBACK] = feedbackCount;
    }
    
    return map;
  }, [notifications]);

  // Recursively apply badge counts to links
  const linksWithBadges = useMemo(() => {
    const applyBadges = (links: SidebarLink[]): SidebarLink[] => {
      return links.map(link => {
        let badgeCount = link.path ? badgeMap[link.path] || 0 : 0;
        
        let processedSubLinks = undefined;
        if (link.subLinks) {
          processedSubLinks = applyBadges(link.subLinks);
          // Aggregate subLink badges to parent if parent doesn't have its own
          if (!badgeCount) {
             badgeCount = processedSubLinks.reduce((sum, sub) => sum + (sub.badgeCount || 0), 0);
          }
        }
        
        return {
          ...link,
          subLinks: processedSubLinks,
          badgeCount: badgeCount > 0 ? badgeCount : undefined
        };
      });
    };
    return applyBadges(navigationLinks);
  }, [navigationLinks, badgeMap]);
  // ----------------------------------------------------------------------------

  // Derive dark mode state and glassmorphism setting
  const isDark = resolvedTheme === 'dark';
  const isGlassEnabled = theme?.aesthetics?.glassmorphism?.enabled;

  // BULLETPROOF: Compute sidebar background color directly from config
  const sidebarBgColor = isDark
    ? theme?.darkMode?.bgSecondary
    : theme?.lightMode?.bgSecondary;

  const [activePath, setActivePath] = useState(location.pathname);
  const [logoError, setLogoError] = useState(false);

  // Resize Logic State (Global)
  const { setSidebarWidth, isResizing, setIsResizing } = uiState;

  // Derive branding info from store
  const logoUrl = general.logoUrl;
  const universityName = general.universityName || CONFIG_DEFAULTS.SYSTEM_IDENTITY.SYSTEM_NAME;
  const isVisualExpanded = !isDesktopSidebarCollapsed || isSidebarHovered;

  // IMPORTANT: Reset error flag when logo changes to reveal new uploads
  useEffect(() => {
    setLogoError(false);
  }, [logoUrl]);

  useEffect(() => {
    setActivePath(location.pathname);
  }, [location.pathname]);

  /**
   * Toggle accordion section — delegates to Zustand store
   * for cross-mount persistence during route navigation.
   */
  const handleToggleAccordion = useCallback((name: string) => {
    toggleSidebarSection(name);
  }, [toggleSidebarSection]);

  const handleOptimisticNavigate = useCallback((path: string) => {
    setActivePath(path);
  }, []);

  /**
   * Auto-expand sections containing the active sub-link.
   * Uses store-based setSidebarSectionOpen which is a no-op
   * if the section is already open — preventing re-renders.
   */
  useEffect(() => {
    navigationLinks.forEach((link) => {
      if (link.subLinks) {
        const hasActiveSubLink = link.subLinks.some(
          (sub) => sub.path && (location.pathname === sub.path || location.pathname.startsWith(sub.path + '/'))
        );
        if (hasActiveSubLink) {
          setSidebarSectionOpen(link.name, true);
        }
      }
    });
  }, [location.pathname, navigationLinks, setSidebarSectionOpen]);

  // Resize Handlers
  const startResizing = useCallback(() => {
    setIsResizing(true);
  }, [setIsResizing]);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, [setIsResizing]);

  const resize = useCallback(
    (mouseMoveEvent: MouseEvent) => {
      if (isResizing && isVisualExpanded) {
        const newWidth = mouseMoveEvent.clientX;
        if (newWidth >= 200 && newWidth <= 480) {
          setSidebarWidth(newWidth);
        }
      }
    },
    [isResizing, isVisualExpanded, setSidebarWidth]
  );

  useEffect(() => {
    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [resize, stopResizing]);

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed', error);
    }
  };

  /**
   * Sidebar container classes.
   * Width is controlled EXTERNALLY by PageContainer — this element is always w-full.
   * Only mobile translate-x and internal layout properties transition here.
   */
  const sidebarClasses = `
    ${isGlassEnabled ? 'glass-panel' : SIDEBAR_BG}
    w-full h-full
    text-theme-sidebar-text shadow-dynamic flex flex-col
    border-r border-theme-divider
    ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
    relative overflow-hidden
  `;



  return (
    <>
      {/* Mobile overlay is rendered by PageContainer — no duplicate here */}

      <aside
        className={sidebarClasses}
        style={{
          backgroundColor: sidebarBgColor,
        }}
        data-testid="nav-sidebar"
      >
        {/* Resize Handle — wider hit area with visible accent on hover for discoverability */}
        {isVisualExpanded && !isMobileSidebarOpen && (
          <div
            className={`absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize z-50
              ${HOVER_TRANSITION} hover:w-2 hover:bg-theme-primary/60
              ${isResizing ? 'bg-theme-primary/80' : 'bg-transparent'}
            `}
            onMouseDown={startResizing}
          />
        )}

        {/* Header — Branding & Toggle */}
        <div className={`py-4 border-b border-theme-divider overflow-hidden ${LAYOUT_TRANSITION} ${isVisualExpanded ? 'px-4' : 'px-2'}`}>
          <div className={`flex items-center w-full ${isVisualExpanded ? 'justify-between' : 'justify-center'} ${LAYOUT_TRANSITION}`}>
            <div className="flex items-center gap-2 overflow-hidden flex-nowrap">
              {logoUrl && !logoError ? (
                <div className={`flex-shrink-0 h-8 ${isVisualExpanded ? 'w-auto max-w-[160px]' : 'w-8'}`}>
                  <LazyImage
                    src={logoUrl}
                    onError={() => setLogoError(true)}
                    alt={getLabel('sidebar_university_logo_alt', 'University Logo')}
                    className="h-8 w-full"
                    imgClassName="object-contain"
                  />
                </div>
              ) : (
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-theme-primary bg-opacity-20 rounded-md">
                  <AcademicCapIcon className="w-5 h-5 text-theme-sidebar-icon" />
                </div>
              )}

              {/* University name — smooth opacity fade */}
              <Typography
                variant="h3"
                weight="bold"
                className={`tracking-tight whitespace-nowrap overflow-hidden ${LAYOUT_TRANSITION} ${isVisualExpanded ? 'opacity-100' : 'opacity-0 w-0'
                  }`}
              >
                {universityName}
              </Typography>
            </div>

            {/* Toggle Button — always in DOM, opacity-controlled */}
            <button
              type="button"
              onClick={() => toggleDesktopSidebar()}
              className={`w-8 h-8 flex items-center justify-center rounded-md border border-theme-sidebar-icon border-opacity-20 hover:bg-theme-primary hover:bg-opacity-10 text-theme-sidebar-icon group flex-shrink-0 ml-2 ${HOVER_TRANSITION} ${isVisualExpanded
                ? 'opacity-100 scale-100'
                : 'opacity-0 scale-75 pointer-events-none absolute'
                }`}
              aria-label={getLabel('sidebar_toggle_aria', 'Toggle Sidebar')}
            >
              <Bars3Icon className={`w-5 h-5 ${HOVER_TRANSITION} group-hover:scale-110`} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        <nav className="flex-1 px-2 py-3 overflow-y-auto overflow-x-hidden custom-scrollbar">
          {isLoading ? (
            <div className="flex justify-center items-center h-20">
              <div className="w-6 h-6 border-2 border-primary-200 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : navigationLinks.length === 0 ? (
            <div className={`text-center p-4 ${isVisualExpanded ? 'opacity-100' : 'opacity-0'} ${LAYOUT_TRANSITION}`}>
              <Typography variant="body-sm" className="text-opacity-60 whitespace-nowrap">
                {getLabel('sidebar_no_links', 'No links available')}
              </Typography>
            </div>
          ) : (
            linksWithBadges.map((link) => (
              <NavItem
                key={link.path || link.name}
                link={link}
                isSidebarExpanded={isVisualExpanded}
                isParentOpen={sidebarOpenSections[link.name] || false}
                toggleParent={handleToggleAccordion}
                currentPath={activePath}
                onNavigate={handleOptimisticNavigate}
              />
            ))
          )}
        </nav>

        {/* Footer — User Profile & Logout */}
        <div className="border-t border-theme-divider p-3 overflow-hidden">
          <div className={`flex items-center gap-3 px-2 py-2 mb-2 ${isVisualExpanded ? '' : 'justify-center'} ${LAYOUT_TRANSITION}`}>
            {user?.avatar ? (
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/20 flex-shrink-0 group hover:border-white transition-colors duration-200">
                <LazyImage
                  src={user.avatar}
                  alt={user.fullName || getLabel('sidebar_common_user', 'User')}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="w-10 h-10 bg-theme-primary rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-200 hover:scale-105 shadow-sm">
                <Typography variant="body-lg" weight="bold">
                  {user?.fullName?.charAt(0).toUpperCase() || 'U'}
                </Typography>
              </div>
            )}

            {/* User info — smooth opacity fade */}
            <div
              className={`flex-1 min-w-0 overflow-hidden ${LAYOUT_TRANSITION} ${isVisualExpanded ? 'opacity-100' : 'opacity-0 w-0'
                }`}
            >
              <Typography variant="body-sm" weight="semibold" className="leading-tight transition-colors" truncate>
                {user?.role === ROLE_KEYS.SUPER_ADMIN ? getLabel('sidebar_roles_super_admin', 'Super Admin') : user?.fullName || getLabel('sidebar_common_user', 'User')}
              </Typography>
              <Typography variant="body-xs" className="text-opacity-70 group-hover:text-opacity-100 transition-colors" truncate>
                {user?.email || 'system@office.edu.et'}
              </Typography>
            </div>
          </div>

          {/* Logout — danger-tinted hover signals destructive action */}
          <button
            type="button"
            onClick={() => handleLogout()}
            title={!isVisualExpanded ? getLabel('sidebar_auth_logout', 'Logout') : undefined}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
              text-theme-sidebar-text text-opacity-80
              hover:bg-red-500/10 hover:text-red-400
              group ${HOVER_TRANSITION} active:scale-[0.98]
              ${isVisualExpanded ? 'justify-start' : 'justify-center'}
            `}
          >
            <ArrowRightOnRectangleIcon className={`w-5 h-5 flex-shrink-0 ${HOVER_TRANSITION} group-hover:scale-[var(--interactive-scale)] group-hover:translate-x-1`} />
            <Typography
              variant="body-sm"
              weight="medium"
              className={`whitespace-nowrap overflow-hidden ${LAYOUT_TRANSITION} ${isVisualExpanded ? 'opacity-100' : 'opacity-0 w-0'
                }`}
            >
              {getLabel('sidebar_auth_logout', 'Logout')}
            </Typography>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
